import { AnimatePresence, motion } from "framer-motion";
import { OnboardingArrowRightIcon } from "../../../assets/icon";
import { Box, Button, Typography, useMediaQuery } from "@mui/material";
import { DotsIndicator } from "../dots-indicator-onboarding/dots-indicator-onboarding";

interface ModalOnboardingProps {
  buttonText: string;
  isNextSlide: boolean;
  handlePrevPage: () => void;
  handleNextSlide: () => void;
  handleNextPage: () => void;
  handleSkip: () => void;
}

export const ModalOnboarding = ({
  isNextSlide,
  buttonText,
  handlePrevPage,
  handleNextSlide,
  handleNextPage,
  handleSkip,
}: ModalOnboardingProps) => {
  const isMobile = useMediaQuery("(max-width:600px)");
  const isCompactLandscape = useMediaQuery(
    "(orientation: landscape) and (max-width: 1366px)",
  );
  const isCompact = isCompactLandscape || isMobile;

  return (
    <Box
      sx={{
        background: "#FFFFFF",
        paddingY: isCompact
          ? { xs: "18px", sm: "22px", md: "26px" }
          : { xs: "36px", sm: "36px", md: "48px" },
        paddingX: isCompact
          ? { xs: "16px", sm: "20px", md: "30px" }
          : { xs: "28px", sm: "28px", md: "64px" },
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        maxWidth: "670px",
        borderRadius: "40px",
        marginX: "8px",
        marginTop: isCompact
          ? { xs: "4px", sm: "8px", md: "10px" }
          : { xs: "8px", sm: "36px", md: "72px" },
      }}
    >
      <AnimatePresence mode="wait" initial={false}>
        {isNextSlide ? (
          <motion.div
            key="nextSlide"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Typography
              sx={{
                mb: 2,
                fontSize: isCompact ? { xs: "24px", sm: "28px" } : "40px",
                fontWeight: 700,
                textAlign: "center",
              }}
            >
              <Box
                component="span"
                sx={{
                  background:
                    "linear-gradient(90deg, #8957CF 0%, #28C9DC 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Control Your Learning
              </Box>
            </Typography>

            <Typography
              sx={{
                mb: isCompact ? 1.5 : 3,
                fontSize: isCompact ? { xs: "12px", sm: "14px" } : "16px",
                textAlign: "center",
                color: "#84909F",
                fontWeight: 600,
                minHeight: isCompact ? "30px" : "50px",
              }}
            >
              The power is yours. Choose to receive lessons at your own pace or
              create one on the spot whenever inspiration strikes. Jump in and
              kickstart your learning adventure!
            </Typography>
          </motion.div>
        ) : (
          <motion.div
            key="prevSlide"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Typography
              sx={{
                mb: 2,
                fontSize: isCompact ? { xs: "24px", sm: "28px" } : "40px",
                fontWeight: 700,
                textAlign: "center",
              }}
            >
              <Box component="span" sx={{ color: "#000" }}>
                {"Interaction &"}{" "}
              </Box>
              <Box
                component="span"
                sx={{
                  background:
                    "linear-gradient(90deg, #8957CF 0%, #28C9DC 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                {"Communication"}
              </Box>
            </Typography>
            <Typography
              sx={{
                mb: isCompact ? 1.5 : 3,
                fontSize: isCompact ? { xs: "12px", sm: "14px" } : "16px",
                textAlign: "center",
                color: "#84909F",
                fontWeight: 600,
                minHeight: isCompact ? "30px" : "50px",
              }}
            >
              I'm your personal coach, ready to support you in real - life
              situations. Curious about presenting our incredible products? Just
              ask! Let's make this journey exciting together!
            </Typography>
          </motion.div>
        )}
      </AnimatePresence>

      <Box>
        <Button
          onClick={isNextSlide ? handleNextPage : handleNextSlide}
          sx={{
            background: "linear-gradient(90deg, #28C9DC 0%, #8957CF 100%)",
            color: "#FFFFFF",
            textTransform: "none",
            fontWeight: "bold",
            borderRadius: "999px",
            px: isCompact ? "22px" : "32px",
            py: isCompact ? "9px" : "16px",
            "&:hover": {
              background: "linear-gradient(90deg, #20B6C8 0%, #7C4ABF 100%)",
            },
          }}
        >
          {buttonText}
        </Button>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          width: "100%",
          marginTop: { xs: "10px", sm: "12px" },
          marginBottom: { xs: "8px", sm: "10px" },
          paddingX: { xs: "8px", sm: "12px" },
          alignItems: "center",
        }}
      >
        <OnboardingArrowRightIcon
          style={{ width: "30px", cursor: "pointer" }}
          onClick={handlePrevPage}
        />
        <DotsIndicator activeIndex={isNextSlide ? 3 : 2} />
        <Typography
          onClick={handleSkip}
          sx={{
            fontSize: { xs: "14px", sm: "16px" },
            color: "#B6BAD5",
            cursor: "pointer",
          }}
        >
          Skip
        </Typography>
      </Box>
    </Box>
  );
};
